/**
 * CreditPurchaseModal Component
 * Modal for purchasing credit packages
 * Handles purchase flow and redirects to Stripe checkout
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CREDIT_PACKAGES } from '../../types/credits';
import { creditsAPI } from '../../services/api/CreditsAPI';
import { useAuth } from '../../hooks/useAuth';
import { trackEvent } from '../../services/metaPixel/metaPixelLoader';
import './CreditPurchaseModal.css';

/**
 * Props for CreditPurchaseModal
 */
interface CreditPurchaseModalProps {
  /** Whether modal is open */
  isOpen: boolean;
  /** Callback to close modal */
  onClose: () => void;
}

/**
 * CreditPurchaseModal component
 * Displays credit packages and handles purchase flow
 * Redirects to Stripe checkout on purchase
 * 
 * @param props - Component props
 * @returns Credit purchase modal component
 */
export function CreditPurchaseModal({ isOpen, onClose }: CreditPurchaseModalProps) {
  const { isAuthenticated, signIn } = useAuth();
  const [purchasingPackage, setPurchasingPackage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingPackageId, setPendingPackageId] = useState<string | null>(null);
  const [shouldReopenAfterAuth, setShouldReopenAfterAuth] = useState(false);

  // Track when the purchase modal is viewed
  useEffect(() => {
    if (isOpen) {
      trackEvent('ViewContent', { content_name: 'Credit Purchase Modal', content_category: 'credits' });
    }
  }, [isOpen]);

  /**
   * Store modal state in sessionStorage when user signs in
   * This ensures we can reopen the modal after auth redirect
   */
  useEffect(() => {
    if (!isAuthenticated && isOpen) {
      // Store that the modal should reopen after auth
      sessionStorage.setItem('creditModalShouldReopen', 'true');
      if (pendingPackageId) {
        sessionStorage.setItem('creditModalPendingPackage', pendingPackageId);
      }
    }
  }, [isAuthenticated, isOpen, pendingPackageId]);

  /**
   * Check if we should reopen modal after authentication
   */
  useEffect(() => {
    if (isAuthenticated) {
      const shouldReopen = sessionStorage.getItem('creditModalShouldReopen');
      const storedPackageId = sessionStorage.getItem('creditModalPendingPackage');
      
      if (shouldReopen === 'true') {
        console.log('[CreditPurchaseModal] User authenticated, reopening modal');
        setShouldReopenAfterAuth(true);
        
        if (storedPackageId) {
          console.log('[CreditPurchaseModal] Found pending package, will complete purchase:', storedPackageId);
          setPendingPackageId(storedPackageId);
        }
        
        // Clear the flags
        sessionStorage.removeItem('creditModalShouldReopen');
        sessionStorage.removeItem('creditModalPendingPackage');
      }
    }
  }, [isAuthenticated]);

  /**
   * If user signs in and there's a pending package, complete the purchase
   */
  useEffect(() => {
    if (isAuthenticated && pendingPackageId && isOpen) {
      console.log('[CreditPurchaseModal] User signed in with pending purchase, completing:', pendingPackageId);
      handlePurchase(pendingPackageId);
      setPendingPackageId(null);
    }
  }, [isAuthenticated, pendingPackageId, isOpen]);

  if (!isOpen && !shouldReopenAfterAuth) {
    return null;
  }

  // If we should reopen after auth but modal is closed, trigger parent to open it
  if (shouldReopenAfterAuth && !isOpen) {
    // This is a signal that the parent should open the modal
    // We'll handle this by not returning null and letting the modal render
    // The parent component should be managing the isOpen state
    console.log('[CreditPurchaseModal] Should reopen after auth but modal is closed');
  }

  /**
   * Handle purchase button click
   * Creates Stripe checkout session and redirects user
   * If not authenticated, stores the package ID and triggers sign in
   */
  const handlePurchase = async (packageId: string) => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      console.log('[CreditPurchaseModal] User not authenticated, storing package and triggering sign in');
      setPendingPackageId(packageId);
      setError(null);
      // Trigger sign in immediately
      signIn();
      return;
    }

    try {
      setPurchasingPackage(packageId);
      setError(null);
      
      const pkg = CREDIT_PACKAGES.find(p => p.id === packageId);
      trackEvent('InitiateCheckout', {
        content_name: pkg?.name ?? packageId,
        content_category: 'credits',
        num_items: 1,
      });
      
      console.log('[CreditPurchaseModal] Initiating purchase for package:', packageId);
      
      // Call API to create Stripe checkout session
      const response = await creditsAPI.purchaseCredits(packageId);
      
      console.log('[CreditPurchaseModal] Checkout session created:', response.sessionId);
      
      // Redirect to Stripe checkout
      if (response.checkoutUrl) {
        window.location.href = response.checkoutUrl;
      } else {
        throw new Error('No checkout URL received from server');
      }
    } catch (error) {
      console.error('[CreditPurchaseModal] Purchase error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(`Purchase failed: ${errorMessage}`);
      setPurchasingPackage(null);
    }
  };

  /**
   * Handle modal backdrop click
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  /**
   * Handle escape key press
   */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const modalContent = (
    <div
      className="credit-purchase-modal-overlay"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-labelledby="credit-modal-title"
      aria-modal="true"
    >
      <div className="credit-purchase-modal">
        {/* Header */}
        <div className="credit-purchase-modal-header">
          <h2 id="credit-modal-title" className="credit-purchase-modal-title">
            <i className="fa-solid fa-coins"></i>
            Purchase Credits
          </h2>
          <button
            className="credit-purchase-modal-close"
            onClick={onClose}
            aria-label="Close modal"
            title="Close"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* Content */}
        <div className="credit-purchase-modal-content">
          {/* Security note */}
          <div className="credit-purchase-security-note">
            <i className="fa-solid fa-shield-halved"></i>
            <div>
              Your payment information is processed securely through Stripe. We <strong><u>never</u></strong> store your credit card details on our servers.
            </div>
          </div>

          {/* Sign in notice (if not authenticated) */}
          {!isAuthenticated && (
            <div className="credit-purchase-signin-notice">
              <div className="credit-purchase-signin-notice-icon">
                🔐
              </div>
              <div className="credit-purchase-signin-notice-title">
                Sign in required
              </div>
              <div className="credit-purchase-signin-notice-text">
                Please sign in to purchase credits. You'll return to this page after signing in.
              </div>
              <button
                className="credit-purchase-signin-btn"
                onClick={() => {
                  console.log('[CreditPurchaseModal] Sign in button clicked');
                  signIn();
                }}
              >
                <i className="fa-solid fa-right-to-bracket"></i>
                Sign In
              </button>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="credit-purchase-error">
              {error}
            </div>
          )}

          {/* Credit packages */}
          <div className="credit-purchase-packages">
            {CREDIT_PACKAGES.map((pkg) => (
              <div
                key={pkg.id}
                className={`credit-package-card ${pkg.popular ? 'credit-package-card--popular' : ''}`}
              >
                {pkg.popular && (
                  <div className="credit-package-badge">
                    ⭐ Most Popular
                  </div>
                )}
                <div className="credit-package-content">
                  <div className="credit-package-info">
                    <div className="credit-package-name">
                      {pkg.name}
                    </div>
                    <div className="credit-package-credits">
                      {pkg.credits} credits
                    </div>
                    {pkg.savings && (
                      <div className="credit-package-savings">
                        {pkg.savings}
                      </div>
                    )}
                  </div>
                  <button
                    className="credit-package-btn"
                    onClick={() => handlePurchase(pkg.id)}
                    disabled={purchasingPackage !== null}
                    aria-label={`Purchase ${pkg.name} for ${pkg.priceDisplay}`}
                  >
                    {purchasingPackage === pkg.id ? 'Processing...' : pkg.priceDisplay}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Money-back guarantee */}
          <div className="credit-purchase-guarantee">
            <i className="fa-solid fa-circle-check"></i>
            <div>
              <strong>Credits never expire.</strong> Use them whenever you're ready.
            </div>
          </div>

          {/* Trust features grid */}
          <div className="credit-purchase-features">
            <div className="credit-purchase-feature">
              <i className="fa-solid fa-bolt"></i>
              <div className="credit-purchase-feature-title">Instant Delivery</div>
              <div className="credit-purchase-feature-text">Credits added immediately</div>
            </div>
            <div className="credit-purchase-feature">
              <i className="fa-solid fa-infinity"></i>
              <div className="credit-purchase-feature-title">Never Expire</div>
              <div className="credit-purchase-feature-text">Use anytime you want</div>
            </div>
            <div className="credit-purchase-feature">
              <i className="fa-solid fa-ban"></i>
              <div className="credit-purchase-feature-title">No Subscriptions</div>
              <div className="credit-purchase-feature-text">One-time purchase only</div>
            </div>
            <div className="credit-purchase-feature">
              <i className="fa-solid fa-user-shield"></i>
              <div className="credit-purchase-feature-title">Privacy First</div>
              <div className="credit-purchase-feature-text">Your data is protected</div>
            </div>
          </div>

          {/* Info note about checkout process */}
          <div className="credit-purchase-info-note">
            You'll be redirected to Stripe's secure checkout page to complete your purchase.
          </div>

          {/* Support contact */}
          <div className="credit-purchase-support">
            Questions? Contact us at <a href="/contact">support</a>
          </div>
        </div>

        {/* Footer */}
        <div className="credit-purchase-modal-footer">
          <div className="credit-purchase-modal-footer-text">
            <i className="fa-solid fa-lock"></i>
            Secure payment powered by Stripe
          </div>
          <div className="credit-purchase-trust-badges">
            <div className="credit-purchase-trust-badge">
              <i className="fa-solid fa-shield-halved"></i>
              <span>Stripe Secured</span>
            </div>
            <div className="credit-purchase-trust-badge">
              <i className="fa-solid fa-lock"></i>
              <span>Encrypted</span>
            </div>
            <div className="credit-purchase-trust-badge">
              <i className="fa-solid fa-ban"></i>
              <span>No Subscriptions</span>
            </div>
          </div>
        </div>

        {/* Safe area for iOS */}
        <div className="credit-purchase-safe-area" />
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
